import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Avatar,
} from '@mui/material';
import api from '../../api';
import { toast } from 'sonner';
import CircularLogoUpload from './LogoUpload';
import { fetchConfig } from '../../pages/utils/fetchConfig';
import EditableEmailField from './EditableEmailField';
import { getAvatarUrl } from '../../pages/utils/avatarCache';

const GeneralSettings = ({ onUserUpdate }) => {
    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem("User");
        return storedUser ? JSON.parse(storedUser) : {};
    });

    const [name, setName] = useState(user.Name);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [maxFileSize, setMaxFileSize] = useState(3);
    const [refreshKey, setRefreshKey] = useState(0); // For logo upload re-render
    const [hasChanges, setHasChanges] = useState(false);
    const [avatarUrl, setAvatarUrl] = useState('');

    const token = JSON.parse(localStorage.getItem("token"))?.token;
    const CONFIG = ['max_logo_size'];

    const getDefaultConfig = async () => {
        try {
            await fetchConfig(CONFIG, ({ data }) => {
                setMaxFileSize(data?.max_logo_size ?? 3);
            });
        } catch (error) {
            console.error("Failed to load configuration. Using default values.");
        }
    };

    const getUser = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get(`/users/profile`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            setUser(response.data.user);
        } catch (error) {
            console.error("Error fetching user profile:", error);
            toast.error("Failed to load user profile");
        } finally {
            setLoading(false);
        }
    }, [token]);

    const handleGetAvatarUrl = useCallback(() => {
        return getAvatarUrl(user);
    }, [user?.ProfilePicture]);

    useEffect(() => {
        getUser();
        getDefaultConfig();
    }, [token]);

    useEffect(() => {
        setName(user.Name || '');
        setAvatarUrl(handleGetAvatarUrl());
    }, [user, handleGetAvatarUrl]);

    useEffect(() => {
        const nameChanged = name !== (user.Name || '');
        const emailChanged = email !== '';
        setHasChanges(nameChanged || emailChanged);
    }, [name, email, user.Name]);

    useEffect(() => {
        const handleStorageChange = () => {
            setAvatarUrl(handleGetAvatarUrl());
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [handleGetAvatarUrl]);

    const updateProfile = async () => {
        if (!hasChanges) {
            toast.error("Please make changes before saving.");
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();

            if (name) formData.append("Name", name);
            if (email) formData.append("Email", email);

            const response = await api.put(`/users/update-profile`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            const updatedUser = response.data.user;
            setUser(updatedUser);
            localStorage.setItem("User", JSON.stringify(updatedUser));
            setHasChanges(false);
            toast.success(response.data.message);
            if (onUserUpdate) onUserUpdate();

        } catch (error) {
            toast.error(error.response?.data?.message || "Error updating profile");
        } finally {
            setLoading(false);
        }
    };

    const handleProfilePictureUpdated = () => {
        // Refresh user object from backend after image change
        getUser();
        setRefreshKey(prev => prev + 1); // force logo refresh
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
            padding: '16px',
            justifyContent: 'space-between',
        }}>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '60%' }}>

                <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                        Name <Typography component="span" color="error">*</Typography>
                    </Typography>
                    <TextField
                        fullWidth
                        placeholder="Your Name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        variant="outlined"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#fff',
                                '& fieldset': { borderColor: '#e0e0e0' },
                                '&:hover fieldset': { borderColor: '#bdbdbd' },
                                '&.Mui-focused fieldset': { borderColor: '#1976d2' }
                            },
                            '& .MuiInputBase-input': {
                                padding: '10px 14px',
                            }
                        }}
                    />
                </Box>

                <EditableEmailField initialEmail={user.Email} />

                <Box>
                    <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem' }}>
                        Role
                    </Typography>
                    <TextField
                        fullWidth
                        value={user.Role}
                        variant="outlined"
                        disabled
                        InputProps={{ readOnly: true }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '8px',
                                backgroundColor: '#f0f0f0',
                            },
                            '& .MuiInputBase-input': {
                                padding: '10px 14px',
                                color: '#757575'
                            }
                        }}
                    />
                </Box>

                {hasChanges && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        You have unsaved changes. Please click "Save Changes" to apply them.
                    </Alert>
                )}

                <Button
                    onClick={updateProfile}
                    variant="contained"
                    sx={{
                        bgcolor: "#010320",
                        color: "#FFF",
                        mt: 2,
                        textTransform: 'none',
                        borderRadius: '8px',
                        padding: '10px 16px',
                        '&:hover': { bgcolor: "#010320" },
                        '&.Mui-disabled': {
                            bgcolor: "#e0e0e0",
                            color: "#9e9e9e",
                        },
                    }}
                    disabled={loading || !hasChanges}
                >
                    {loading ? (
                        <CircularProgress size={24} sx={{ color: "#FFF" }} />
                    ) : (
                        "Save Changes"
                    )}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '30%', alignItems: 'center' }}>
                <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '0.875rem', textAlign: 'center', width: 160 }}>
                    Profile Picture
                </Typography>

                <CircularLogoUpload
                    key={refreshKey}
                    previewUrl={user.ProfilePicture && user.ProfilePicture !== "null" ? user.ProfilePicture : null}
                    onLogoChange={handleProfilePictureUpdated}
                    maxFileSize={maxFileSize}
                    setRemoveProfile={handleProfilePictureUpdated}
                    recommended="256x256px or larger, PNG or JPEG"
                />
            </Box>
        </Box>
    );
};

export default GeneralSettings;
